// Auto-generado por @xone-web/staticweb v0.13.0 (MPA Island mode).
// Pagina: LoginColl
// NO EDITAR: se regenera en cada build.

import { createAppContext, shouldRunCreateLifecycle } from "@xone-web/app-orchestrator";
import { ScriptDateConstructor as __XoneDate } from "@xone-web/types";
import { registerXoneIsland, registerXoneCombo, installCanvasSizerFromDocument, hydrateVisibleContentsNow } from "@xone-web/islands-runtime";
import * as events from "./LoginColl.events";
import * as __functions from "./functions";
import { ROUTE_MAP } from "./routes";
import { APP_CONFIG } from "./app-config";
import { COLL_DEF } from "./LoginColl.colldef";


const api = createAppContext({
    routeMap: ROUTE_MAP,
    apiBase: APP_CONFIG.apiBase,
    backend: (APP_CONFIG as { backend?: "webcore" | "generated" }).backend ?? "generated",
    filterParser: (APP_CONFIG as { filterParser?: "simple" | "full" }).filterParser ?? "simple",
    tablePrefix: APP_CONFIG.tablePrefix,
    appName: APP_CONFIG.name,
    appVersion: APP_CONFIG.version,
    projectId: APP_CONFIG.projectId,
    collectionName: "LoginColl",
    collectionDefinition: COLL_DEF as unknown as Record<string, unknown>,
});

// Bake entry-point y login collection para navegacion post-login
api.macros.set("ENTRY_POINT", APP_CONFIG.entryPoint);
if (APP_CONFIG.loginCollection) {
    api.macros.set("LOGIN_COLLECTION", APP_CONFIG.loginCollection);
}

// Registrar objetos API XOne como globals en window para que
// functions.js pueda acceder a ui, self, appData, $http, crypto,
// console como variables globales (paridad sandbox XOne).
// #366: el Date de XOne viaja por api hasta el destructuring de cada handler de
// evento (ver wrapEvent). NO se toca window.Date: la plataforma conserva el
// nativo — la web legacy, en cambio, parchea Date.prototype.toString global.
(api as unknown as Record<string, unknown>).Date = __XoneDate;

const __w = window as unknown as Record<string, unknown>;
__w.ui = api.ui;
__w.self = api.self;
__w.This = api.This;
__w.ThisDataColl = api.ThisDataColl;
__w.appData = api.appData;
__w.$http = api.$http;
// window.crypto is read-only (Web Crypto API) — use xoneCrypto alias
Object.defineProperty(__w, "xoneCrypto", { value: api.crypto, configurable: true });
try { Object.defineProperty(__w, "crypto", { value: api.crypto, configurable: true }); } catch { /* read-only in browser */ }
// clipboard: portapapeles del sistema (paridad objeto de script XOne `Clipboard`).
// Los proyectos lo usan como global pelado, p.ej. `clipboard.setText(campo)`.
__w.clipboard = api.clipboard;
__w.Clipboard = api.clipboard;
__w.Bundle = api.Bundle;
__w.console = api.console;
// Globales de plataforma user y empresa (paridad Android: singletons vivos toda la
// sesión). Son Proxies con overlay PERSISTIDO en sessionStorage (ver
// persistentGlobal.ts), así que `user.MAP_TOKEN = …` / `ent.setVariables(…)` sobreviven
// a la navegación MPA. `ent` es el alias corto idiomático de `enterprise` (Android lo
// expone solo vía appData.getCurrentEnterprise(); en web lo publicamos como global por
// conveniencia). Misma instancia → escrituras a cualquiera de los tres autopersisten.
__w.user = api.appData.getCurrentUser() ?? {};
__w.enterprise = api.appData.getCurrentEnterprise() ?? {};
__w.ent = __w.enterprise;

// `getContents(name)` global: en XOne un `getContents` pelado dentro de una
// función global de functions.js (sin receptor) opera sobre el objeto/página
// en curso. En el MPA ese objeto es `self` (la página), cuyos <contents>
// incluyen los del layout-shell fusionados (p.ej. WebMenuPadre/WebMenuHijo).
// Paridad Android `XoneDataObject.getContents`; síncrono (devuelve el wrapper
// compartido o null si el <contents> no está declarado).
__w.getContents = (nameOrIndex) => {
    const s = api.self;
    return s && typeof s.getContents === "function" ? s.getContents(nameOrIndex) : null;
};

// Registrar funciones globales de functions.js en window para que
// los eventos transpilados puedan llamarlas como bare identifiers.
for (const [k, v] of Object.entries(__functions)) {
    if (typeof v === "function") __w[k] = v;
}

registerXoneIsland({ api, events: events });
registerXoneCombo();

// Sizer responsive (F1 §1.A): fija --xone-p-unit/--xone-font-unit en px sobre
// el .xone-canvas para desacoplarlas de cqw, permitiendo containerizar frames
// sin romper el escalado 'p'. No-op en proyectos legacy (sin --xone-base-width).
// Se instala ASAP (módulo deferred = DOM ya parseado) para minimizar FOUC.
installCanvasSizerFromDocument();

const handlers = events as Record<string, ((api: unknown, ...args: unknown[]) => Promise<unknown>) | undefined>;
// self global del modelo XOne: las funciones globales del proyecto referencian
// 'self' (el objeto del evento actual). Lo fijamos antes de cada handler de
// lifecycle = el self de la pagina.
const __setXoneSelf = (__functions as Record<string, unknown>)["__xoneSetSelf"] as ((v: unknown) => void) | undefined;
// Inyectar el document REAL en el sandbox del módulo de funciones: el shim
// reenvía SOLO 'document.title' (get/set) al document del navegador, para que
// el proyecto pueda fijar el título de pestaña (WEAL-37). Debe cablearse ANTES
// del primer invoke() para que el after-edit ya lo tenga disponible.
const __setXoneRealDoc = (__functions as Record<string, unknown>)["__xoneSetRealDocument"] as ((d: unknown) => void) | undefined;
__setXoneRealDoc?.(document);
// #366: Date de XOne para el JS de proyecto. El modulo de funciones declara el
// binding sombreado (let Date) y lo inyectamos AQUI, antes del primer invoke(),
// igual que el document real. Los cuerpos de evento lo reciben por api (abajo).
// Sin esto, "F > '" + new Date() + "'" manda al backend la fecha larga del
// navegador, que MySQL no parsea: el filtro deja de filtrar EN SILENCIO.
const __setXoneDateCtor = (__functions as Record<string, unknown>)["__xoneSetDateCtor"] as ((d: unknown) => void) | undefined;
__setXoneDateCtor?.(__XoneDate);
async function invoke(name: string): Promise<void> {
    const fn = handlers[name];
    if (typeof fn === "function") {
        __setXoneSelf?.((api as { self?: unknown }).self);
        await fn(api);
    }
}



document.addEventListener("DOMContentLoaded", async () => {
    // Ciclo de vida XOne (paridad EditViewHyper.java):
    //   load (onload, objeto traído del repositorio) → create (al crearlo) →
    //   before-edit (ANTES de renderizar) → [render: se abre el gate y los
    //   <contents> rendican] → after-edit (UNA VEZ finalizado el render).
    // Android dispara after-edit "después de pintar toda la pantalla"
    // (EditViewHyper.java:2706 doLoadTabItemsFinish → sendAfterEditMessage),
    // por eso after-edit corre DESPUÉS de signalLifecycleReady. before-edit va
    // antes (puede fijar el filtro del contents).
    try {
        await invoke("load");
        // WEAL-267: <create> (paridad Android OnCreate) SÓLO para un objeto NUEVO.
        // Al abrir la edición de un registro EXISTENTE (drill-down desde un
        // listado vía appData.pushValue → self.isNew()===false) NO debe correr:
        // reejecuta los <setval> de defaults (p.ej. TIPOENTIDAD_COD=0 en Entidades)
        // y pisa el valor ya cargado, vaciando el combo enlazado (MAP_TIPOENTIDAD).
        // Si el runtime no puede determinar isNew(), se mantiene el flujo previo.
        if (shouldRunCreateLifecycle((api as { self?: unknown }).self)) {
            await invoke("create");
        }
        await invoke("beforeEdit");
    } catch (err) {
        console.error("[xone] before-edit lifecycle error in LoginColl:", err);
    } finally {
        // Abrir el gate → arranca el render de los <contents>.
        api.signalLifecycleReady?.();
        // Prefetch coordinado: hidrata los <contents> YA visibles en un solo
        // bucle síncrono (antes del primer paint, ganando al IO por-isla) → sus
        // loadAll caen en la misma ráfaga y el coalescer del cliente los funde en
        // un único POST /DoBatch en vez de N peticiones en cascada. NO toca los
        // contents ocultos (tabs/paneles/floating) ni los de debajo del fold:
        // esos siguen difiriéndose. Idempotente y best-effort.
        try { hydrateVisibleContentsNow(); } catch (err) {
            console.error("[xone] hydrateVisibleContentsNow error:", err);
        }
    }
    // after-edit: tras disparar el render. Los controles estáticos (botones/
    // inputs/labels) ya están en el DOM desde DOMContentLoaded, así que
    // setFieldPropertyValue (p.ej. el title de BTVERSION) se refleja; el sync
    // final propaga los valores calculados a spans/inputs.
    try {
        await invoke("afterEdit");
        api.domSync.syncAll();
    } catch (err) {
        console.error("[xone] after-edit lifecycle error in LoginColl:", err);
    }
    
    // Reset dirty AFTER lifecycle + island hydration. setTimeout(0) ensures
    // any pending microtasks from island bind events complete first.
    // Only then register the beforeunload guard.
    setTimeout(() => {
        const s = api.self as unknown as { _dirty?: { setOriginal?: (f: Map<string, unknown>) => void } };
        if (s._dirty?.setOriginal) {
            s._dirty.setOriginal(new Map(Object.entries(api.self.toJson())));
        }
        window.addEventListener("beforeunload", (e) => {
            // Navegación programática XOne (openCollection, redirect post-login,
            // back): NO avisar — paridad Android. Además el diálogo durante una
            // View Transition cross-document congela el rendering de la página
            // destino (rAF/IO suspendidos, islands sin hidratar).
            const w = window as unknown as Record<string, unknown>;
            if (w.__xoneProgrammaticNav === true) return;
            if (typeof api.self.getDirty === "function" && api.self.getDirty()) {
                e.preventDefault();
            }
        });
    }, 0);
});

// bfcache pageshow handler ya registrado por createAppContext —
// no duplicar aqui.

if (typeof window !== "undefined") {
    (window as unknown as Record<string, unknown>).__xone = { collection: "LoginColl", api, events };
}
